import React, { useMemo, useState } from 'react'
import { CAlert, CBadge, CButton, CCard, CCardBody, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import moment from 'moment'
import Bootbox from 'bootbox-react'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import BonusCardForm from './BonusCardForm'
import BonusPointsForm from './BonusPointsForm'
import { isSetUp } from './bonusCardUtils'

const SEARCH_FIELDS = [
  { key: 'bonus_card_name', label: 'Bonus Card', _style: { width: '10%' } },
  { key: 'round', label: 'Round', _style: { width: '10%' } },
]

const columns = [
  { key: 'round', label: 'Round', filter: false, _style: { width: '10%' } },
  { key: 'duration', label: 'Duration', filter: false, sorter: false, _style: { width: '18%' } },
  { key: 'bonus_card_name', label: 'Bonus Card', filter: false, _style: { width: '10%' } },
  { key: 'status', label: 'Status', filter: false, _style: { width: '10%' } },
  { key: 'actions', label: '', filter: false, sorter: false, _style: { width: '10%' } },
]

const BonusTable = ({ rounds, loading, error, fetchData }) => {
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [setUpRound, setSetUpRound] = useState(null)
  const [pointsRound, setPointsRound] = useState(null)
  const [removeRound, setRemoveRound] = useState(null)

  // The endpoint returns every round in one call, so search and paging happen here.
  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return rounds
    return rounds.filter((row) =>
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [rounds, searchField, searchValue])

  const removeCard = () => {
    const round = removeRound
    setRemoveRound(null)
    CommonService.revertBonusCard({ teamPowerId: round.team_power_id })
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          fetchData()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Could not remove the bonus card', 'error'))
  }

  const noItemsLabel = searchValue.trim()
    ? 'No rounds match your search'
    : 'No rounds yet - add rounds before setting up bonus cards'

  return (
    <CCard className="bc-panel">
      <CCardBody>
        <div className="bc-panel-heading">
          <h2 className="bc-panel-title">Round Bonus Cards</h2>
          <p className="bc-panel-note">
            One bonus card per round. Set it up, then award its players bonus points once the round
            is played.
          </p>
        </div>

        {error ? (
          <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
            <CIcon icon={cilWarning} className="flex-shrink-0" />
            <span>We couldn&apos;t load the rounds.</span>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              className="ms-auto"
              onClick={fetchData}
            >
              <CIcon icon={cilReload} className="me-1" />
              Retry
            </CButton>
          </CAlert>
        ) : (
          <>
            <TableSearchToolbar
              fields={SEARCH_FIELDS}
              field={searchField}
              onFieldChange={(field) => {
                setSearchField(field)
                setSearchValue('')
              }}
              value={searchValue}
              onSearch={setSearchValue}
              placeholder="Search..."
            />

            <div className="ge-datatable ge-datatable--no-select">
              <CSmartTable
                columns={columns}
                items={items}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect
                loading={loading}
                noItemsLabel={noItemsLabel}
                pagination
                columnSorter
                scopedColumns={{
                  round: (item) => (
                    <td>
                      <span className="bc-round">R{item.round}</span>
                    </td>
                  ),
                  duration: (item) => (
                    <td className="bc-muted">
                      {moment(item.start_date).format('MMM D')} -{' '}
                      {moment(item.end_date).format('MMM D')}
                    </td>
                  ),
                  bonus_card_name: (item) => (
                    <td>
                      {isSetUp(item) ? (
                        <>
                          <div className="bc-card-name">{item.bonus_card_name}</div>
                          {item.decription && (
                            <div className="bc-card-desc" title={item.decription}>
                              {item.decription}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="bc-muted">No bonus card yet</span>
                      )}
                    </td>
                  ),
                  status: (item) => (
                    <td>
                      <CBadge shape="rounded-pill" color={isSetUp(item) ? 'success' : 'secondary'}>
                        {isSetUp(item) ? 'Set up' : 'Not set up'}
                      </CBadge>
                    </td>
                  ),
                  actions: (item) => (
                    <td className="text-center">
                      <RowActionsMenu
                        actions={[
                          {
                            label: isSetUp(item) ? 'Edit Bonus Card' : 'Set Up',
                            onClick: () => setSetUpRound(item),
                          },
                          {
                            label: 'Bonus Points',
                            hidden: !isSetUp(item),
                            onClick: () => setPointsRound(item),
                          },
                          {
                            label: 'Remove Bonus Card',
                            hidden: !isSetUp(item),
                            danger: true,
                            onClick: () => setRemoveRound(item),
                          },
                        ]}
                      />
                    </td>
                  ),
                }}
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </>
        )}
      </CCardBody>

      <BonusCardForm round={setUpRound} onClose={() => setSetUpRound(null)} onSaved={fetchData} />
      <BonusPointsForm
        round={pointsRound}
        onClose={() => setPointsRound(null)}
        onSaved={fetchData}
      />
      <Bootbox
        show={!!removeRound}
        type={'confirm'}
        message={`Remove the Round ${removeRound?.round} bonus card? Its players and any bonus points awarded to them will be deleted.`}
        onSuccess={removeCard}
        onCancel={() => setRemoveRound(null)}
        onClose={() => setRemoveRound(null)}
      />
    </CCard>
  )
}

export default BonusTable
